import { useState, useEffect } from 'react';
import { useSession } from '@/auth/index';
import { useCurrentUserApiAccess } from '@/components/ApiDashboard/hooks/useCurrentUserApiAccess/useCurrentUserApiAccess';
import { NotesCount } from '@/components/RecentReportsCard/components';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAccountNotes } from '@/dashboard/AccountNotes/useAccountNotes';
import { pdfModalsState, usePdf } from '@/reports/index';
import { useReportDelete } from '@/reports/useReportDelete/useReportDelete';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import {
  Icons,
  CustomMenu,
  styled,
  useMediaQuery,
  useTheme,
} from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { scrollToElement } from '@/utils/index';
import { PdfUpgradeWrapper } from '../../PdfAddon/PdfUpgradeWrapper';

const StyledMenu = styled(CustomMenu)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: '100%',
  lineHeight: 1.2,
  gap: theme.spacing(0.5),
  fontSize: '0.875rem',
  padding: theme.spacing(0.25, 0.75),
  minWidth: '65px',

  [theme.breakpoints.between(0, 375)]: {
    minWidth: '30px',
  },

  '.MuiSvgIcon-root': {
    fontSize: '1rem',
  },

  [theme.breakpoints.up('sm')]: {
    minWidth: '64px',
    fontWeight: 400,
    padding: theme.spacing(1.3125, 1.25),
    '.MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  },
}));

export function MoreAction({
  isInReport,
  reportPermaLink,
  reportType,
  hidePdf,
  hideNotes,
  reportUpgraded,
  deleteCurrentReport,
}: {
  isInReport?: boolean;
  reportPermaLink: string;
  reportType: ValidReportTypes;
  hidePdf?: boolean;
  hideNotes?: boolean;
  reportUpgraded?: boolean;
  deleteCurrentReport: (permalink: string) => void;
}) {
  const { redirectToReportUrl } = useReportRedirect();
  const { reportNotesCount } = useAccountNotes(reportPermaLink);
  const reportDeleteMutation = useReportDelete(
    reportPermaLink,
    deleteCurrentReport
  );
  const { currentUserHasApiAccess } = useCurrentUserApiAccess();
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfModalState, setPdfModalState] = useState<pdfModalsState>(
    pdfModalsState.None
  );
  const [hideSections, setHideSections] = useState<string[]>([]);
  const { refetch } = usePdf(reportPermaLink, false, hideSections);
  const { logError, trackEvent } = useAppConfigContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isXSMob = useMediaQuery(theme.breakpoints.between(0, 375));
  const isPersonReport = reportType.toLowerCase() === 'person';
  const isVehicleReport = reportType.toLowerCase() === 'vehicle';
  const isObituaryReport = reportType.toLowerCase() === 'obituary';
  const isUnclaimedMoneyReport = reportType.toLowerCase() === 'unclaimed-money';
  const { session } = useSession();

  useEffect(() => {
    if (hideSections.length > 0 && isVehicleReport) {
      generatePdf();
    }
  }, [hideSections]);

  function notesTotalDisplay() {
    const notesTotal = reportNotesCount();
    if (notesTotal === null) return null;
    return <NotesCount>{notesTotal}</NotesCount>;
  }

  function handleReportDelete() {
    reportDeleteMutation.mutate();
  }

  const handlerPDFFunction = () => {
    generatePdf();
  };

  async function generatePdf() {
    if (
      !session?.account?.account.subscription_info.subscription_features
        .pdf_access
    ) {
      setPdfModalState(pdfModalsState.AddonCheckoutModal);
      return;
    }
    setGeneratingPdf(true);
    const blankWindow = window.open(
      '',
      '_blank',
      'location=yes,height=800,width=600'
    );
    if (!blankWindow) {
      setGeneratingPdf(false);
      const error = new Error('popup_blocked');
      logError('Error generating pdf for report', error);
      return;
    }
    const generatingPdf = await refetch();

    if (blankWindow.location) {
      blankWindow.location = generatingPdf?.data || '';
    }
    setGeneratingPdf(false);
  }

  function generateCustomPdf() {
    setHideSections(['market_value', 'sales']);
  }

  const moreMenuItems = [
    {
      display: 'PDF',
      action: generatePdf,
      icon: <Icons.PictureAsPdf fontSize="small" color="primary" />,
      disabled: generatingPdf,
      hidden:
        !isMobile || hidePdf || isObituaryReport || isUnclaimedMoneyReport,
    },
    {
      display: 'Modified PDF',
      action: generateCustomPdf,
      icon: <Icons.PictureAsPdf fontSize="small" color="primary" />,
      disabled: generatingPdf,
      hidden: !currentUserHasApiAccess() || !isVehicleReport,
    },
    {
      display: 'Notes',
      action: isInReport
        ? () => {
            trackEvent('notes', 'engagement click', 'rats component');
            scrollToElement('Notes-section');
          }
        : () => {
            trackEvent('notes', 'engagement click', 'rats component');
            redirectToReportUrl(reportType, `${reportPermaLink}#Notes-section`);
          },
      icon: <Icons.Chat fontSize="small" color="primary" />,
      count: notesTotalDisplay(),
      hidden: hideNotes,
    },
    {
      display: reportUpgraded ? 'Unlocked' : 'Free Bonus',
      action: () => scrollToElement('additional-owner-details-section'),
      icon: reportUpgraded ? (
        <Icons.LockOpen fontSize="small" color="primary" />
      ) : (
        <Icons.Star fontSize="small" color="primary" />
      ),
      hidden: !isPersonReport,
      disabled: reportUpgraded,
    },
    {
      display: 'Delete',
      // Placeholder action
      action: () => handleReportDelete(),
      icon: <Icons.Close fontSize="small" color="primary" />,
      hidden: isInReport,
    },
  ];

  if (reportType === 'username') {
    const deleteIndex = moreMenuItems.findIndex(
      (item) => item.display === 'Delete'
    );
    if (deleteIndex !== -1) {
      moreMenuItems.splice(deleteIndex, 1);
    }
  }

  const handleTrackEvent = () => {
    trackEvent(
      'overflow menu entry point',
      'engagement click',
      'rats component'
    );
  };

  return (
    <>
      <StyledMenu
        showIcon
        title="More"
        menuItems={moreMenuItems}
        buttonText={isXSMob ? '' : 'More'}
        variant="contained"
        color="info"
        disableElevation
        handleTrackEvent={handleTrackEvent}
        disabled={reportDeleteMutation.isLoading}
      />
      <PdfUpgradeWrapper
        modalState={pdfModalState}
        setModalState={setPdfModalState}
        reportPermalink={reportPermaLink}
        generatePDF={handlerPDFFunction}
      />
    </>
  );
}
